/**
 * shadcn/Tailwind replacement for MarkdownPreview.
 * Renders markdown as HTML using react-markdown with GFM, syntax highlighting,
 * mermaid diagrams, content link interception, and anchor navigation.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { MermaidDiagram } from './MermaidDiagram';
import { isContentLink, resolveContentLink } from '../../services/ContentLinkResolver';
import { debugVerboseLog } from '../../utils/debugLog';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export interface ShadcnMarkdownPreviewProps {
    content: string;
    emptyMessage?: string;
    className?: string;
    style?: React.CSSProperties;
    debugAnchors?: boolean;
    scrollContainerRef?: React.RefObject<HTMLElement | null>;
    onContentLinkClick?: (entityId: string, displayName: string) => void;
}

function getTextFromReactNode(node: any): string {
    if (node == null) return '';
    if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') return String(node);
    if (Array.isArray(node)) return node.map(getTextFromReactNode).join('');
    if (typeof node === 'object' && 'props' in node) {
        return getTextFromReactNode((node as any).props?.children);
    }
    return '';
}

function slugifyHeading(text: string): string {
    return text
        .trim()
        .toLowerCase()
        .replace(/['"]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 128);
}

function resolveAnchorTarget(id: string, scope: ParentNode): HTMLElement | null {
    const direct =
        document.getElementById(id) ??
        document.getElementById(id.toLowerCase()) ??
        document.getElementById(id.toLowerCase().replace(/\s+/g, '-'));
    if (direct) return direct as HTMLElement;

    const safePrefix = id.replace(/"/g, '\\"');
    return (scope.querySelector?.(`[id^="${safePrefix}-"]`) as HTMLElement | null) ?? null;
}

function scrollToAnchorTarget(target: HTMLElement, container: HTMLElement | null, debugAnchors: boolean): void {
    if (container && container.contains(target)) {
        const containerRect = container.getBoundingClientRect();
        const elRect = target.getBoundingClientRect();
        const nextTop = container.scrollTop + (elRect.top - containerRect.top) - 8;

        if (debugAnchors) {
            debugVerboseLog('🔗 ShadcnMarkdownPreview scrolling container', {
                containerScrollTopBefore: container.scrollTop,
                nextTop,
            });
        }

        try {
            container.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' as ScrollBehavior });
        } catch { /* ignore */ }
        container.scrollTop = Math.max(0, nextTop);
        return;
    }

    try {
        target.scrollIntoView({ block: 'start', behavior: 'smooth' as ScrollBehavior });
    } catch {
        target.scrollIntoView({ block: 'start' });
    }
}

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export const ShadcnMarkdownPreview: React.FC<ShadcnMarkdownPreviewProps> = ({
    content,
    emptyMessage = 'No content to display',
    className,
    style,
    debugAnchors = false,
    scrollContainerRef,
    onContentLinkClick,
}) => {
    const { isDarkMode } = useTheme();
    const codeStyle = isDarkMode ? vscDarkPlus : vs;
    const slugCounts = new Map<string, number>();
    const headingLogCountRef = React.useRef(0);

    const renderHeading = (level: HeadingLevel) => {
        return ({ node: _node, children, ...props }: any) => {
            const text = getTextFromReactNode(children);
            const base = slugifyHeading(text) || 'section';
            const next = (slugCounts.get(base) ?? 0) + 1;
            slugCounts.set(base, next);
            const id = next === 1 ? base : `${base}-${next - 1}`;

            if (debugAnchors && headingLogCountRef.current < 5) {
                headingLogCountRef.current += 1;
                debugVerboseLog('🔗 ShadcnMarkdownPreview heading id', { level: `h${level}`, text, id });
            }

            const Tag = (`h${level}` as unknown) as keyof JSX.IntrinsicElements;
            return <Tag id={id} {...props}>{children}</Tag>;
        };
    };

    if (!content || content.trim() === '') {
        return (
            <div className={cn('p-4 text-center', className)} style={style}>
                <p className="text-sm text-muted-foreground">{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'markdown-body rounded-md border border-border bg-card p-4 text-foreground text-sm leading-relaxed max-w-none',
                className,
            )}
            style={style}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    a({ href, children, ...props }: any) {
                        const rawHref = typeof href === 'string' ? href : '';
                        const hasFragment = rawHref.includes('#');
                        return (
                            <a
                                href={rawHref}
                                {...props}
                                onClick={(e) => {
                                    props?.onClick?.(e);
                                    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

                                    if (onContentLinkClick && isContentLink(rawHref)) {
                                        e.preventDefault();
                                        const linkEl = e.currentTarget as HTMLAnchorElement;
                                        linkEl.style.opacity = '0.5';
                                        linkEl.style.cursor = 'wait';
                                        resolveContentLink(rawHref)
                                            .then((resolved) => {
                                                linkEl.style.opacity = '';
                                                linkEl.style.cursor = '';
                                                if (resolved) {
                                                    onContentLinkClick(resolved.id, resolved.displayName);
                                                } else {
                                                    linkEl.title = `Document not found: ${rawHref}`;
                                                    linkEl.style.textDecoration = 'line-through';
                                                    setTimeout(() => {
                                                        linkEl.style.textDecoration = '';
                                                        linkEl.title = '';
                                                    }, 2000);
                                                }
                                            })
                                            .catch(() => {
                                                linkEl.style.opacity = '';
                                                linkEl.style.cursor = '';
                                            });
                                        return;
                                    }

                                    if (!hasFragment) return;

                                    const fragment = rawHref.split('#').pop() ?? '';
                                    const rawId = decodeURIComponent(fragment);
                                    if (!rawId) return;

                                    e.preventDefault();
                                    const container = scrollContainerRef?.current;
                                    const scope: ParentNode = container ?? document;
                                    const resolvedEl = resolveAnchorTarget(rawId, scope);

                                    if (resolvedEl) {
                                        scrollToAnchorTarget(resolvedEl, container ?? null, debugAnchors);
                                        try {
                                            if (!resolvedEl.hasAttribute('tabindex')) resolvedEl.setAttribute('tabindex', '-1');
                                            (resolvedEl as any).focus?.({ preventScroll: true });
                                        } catch { /* ignore */ }
                                    }
                                }}
                            >
                                {children}
                            </a>
                        );
                    },
                    h1: renderHeading(1),
                    h2: renderHeading(2),
                    h3: renderHeading(3),
                    h4: renderHeading(4),
                    h5: renderHeading(5),
                    h6: renderHeading(6),
                    code({ node, inline, className: codeClassName, children, ...props }: any) {
                        const match = /language-(\w+)/.exec(codeClassName || '');
                        const language = match ? match[1] : '';
                        if (!inline && language === 'mermaid') {
                            return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />;
                        }
                        return !inline && match ? (
                            <SyntaxHighlighter
                                style={codeStyle}
                                language={language}
                                PreTag="div"
                                {...props}
                            >
                                {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                        ) : (
                            <code className={codeClassName} {...props}>
                                {children}
                            </code>
                        );
                    },
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};
